import React, { useState, useEffect } from 'react';
import { Settings } from 'lucide-react';
import './ThingsBoardSettings.css';

const ThingsBoardSettings = () => {
  const [settings, setSettings] = useState({
    url: '',
    username: '',
    password: ''
  });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await fetch('/api/admin/thingsboard/settings', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSettings({
          url: data.url || '',
          username: data.username || '',
          password: '' // Never load password from server
        });
      }
    } catch (err) {
      console.error('Failed to load settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    setError('');
    setSuccess('');

    try {
      const response = await fetch('/api/admin/thingsboard/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(settings)
      });

      const data = await response.json();

      if (response.ok) {
        setTestResult('success');
        setSuccess('✅ Connection successful!');
      } else {
        setTestResult('error');
        setError('❌ ' + (data.error || 'Connection failed. Please check your credentials.'));
      }
    } catch (err) {
      setTestResult('error');
      setError('❌ Connection test failed: ' + err.message);
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch('/api/admin/thingsboard/settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        setSuccess('✅ ThingsBoard settings saved successfully!');
      } else {
        const data = await response.json();
        setError('❌ Failed to save settings: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      setError('❌ Failed to save settings: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading settings...</div>;
  }

  return (
    <div className="thingsboard-settings-card">
      <div className="settings-header">
        <Settings size={24} />
        <h3>ThingsBoard Configuration</h3>
      </div>

      <p className="settings-description">
        Configure your ThingsBoard server connection. This is required to create users,
        tenants, and fetch device data.
      </p>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="settings-form">
        <div className="form-group">
          <label>ThingsBoard URL *</label>
          <input
            type="url"
            value={settings.url}
            onChange={(e) => setSettings({ ...settings, url: e.target.value })}
            placeholder="http://your-thingsboard-server:8080"
          />
          <small>Example: http://192.168.1.100:8080 or https://thingsboard.example.com</small>
        </div>

        <div className="form-group">
          <label>Admin Username *</label>
          <input
            type="text"
            value={settings.username}
            onChange={(e) => setSettings({ ...settings, username: e.target.value })}
            placeholder="sysadmin@thingsboard.org"
          />
          <small>System administrator or tenant admin username</small>
        </div>

        <div className="form-group">
          <label>Admin Password *</label>
          <input
            type="password"
            value={settings.password}
            onChange={(e) => setSettings({ ...settings, password: e.target.value })}
            placeholder="Enter password"
          />
        </div>

        <div className="settings-actions">
          <button
            className="btn btn-secondary"
            onClick={handleTest}
            disabled={testing || !settings.url || !settings.username || !settings.password}
          >
            {testing ? 'Testing...' : 'Test Connection'}
          </button>

          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving || !settings.url || !settings.username || !settings.password}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      <div className="settings-info">
        <h4>ℹ️ Important Notes:</h4>
        <ul>
          <li>ThingsBoard server must be accessible from this application</li>
          <li>
            <strong>Docker Users:</strong> If ThingsBoard is running in a separate container,
            use <code>http://host.docker.internal:9090</code> instead of <code>http://localhost:9090</code>
          </li>
          <li>Admin credentials are used to create tenants and users automatically</li>
          <li>Credentials are stored securely in environment variables (runtime only)</li>
          <li>Test the connection before saving</li>
        </ul>
      </div>
    </div>
  );
};

export default ThingsBoardSettings;
